import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  useBreakpointValue,
  Spinner
} from "@chakra-ui/react";
import SearchAdaccount from "components/bmControl/SearchAdaccount";
import AdSetsTable from "components/campaignTables/AdSetsTable";
import AdsTable from "components/campaignTables/AdsTable";
import AllAdSetsTable from "components/campaignTables/AllAdsetsTable";
import AllAdsTable from "components/campaignTables/AllAdsTable";
import AllCampaignsTable from "components/campaignTables/AllCampaignsTable";
import CampaignsTable from "components/campaignTables/CampaignsTable";
import DateRangeSelector from "components/campaignTables/DateRangeSelector";
import SidebarWithHeader from "components/SidebarWithHeader";
import { toZonedTime } from "date-fns-tz";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { MdClose, MdFolderOpen, MdOutlineFolderSpecial, MdWindow } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGetBmAdAccountsCountQuery, useGetBmAdAccountsQuery } from "store/BmAccounts/bmAccountApi";
import {
  selectAdAccountAssigned,
  selectSelectedAdAccounts
} from "store/BmAccounts/bmAccountSlice";
import {
  selectAccessAllAdAccounts,
  selectSelectedAdsetId,
  selectSelectedAdsId,
  selectSelectedCampaignId,
  setAdsetsForInsights,
  setAfter,
  setBefore,
  setCampaignsForInsights,
  setCurrentPage,
  setPresentPage,
  setSelectedAdset,
  setSelectedAdsetId,
  setSelectedAdsId,
  setSelectedCampaignId,
  setSelectedCampaigns,
} from "store/campaigns/campaignsSlice";

function CampaignsForClient() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const toast = useToast();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("business_id");
  const selectedCampaignId = useSelector(selectSelectedCampaignId);
  const selectedAdsetId = useSelector(selectSelectedAdsetId);
  const selectedAdsId = useSelector(selectSelectedAdsId);
  const selectedAdAccounts = useSelector(selectSelectedAdAccounts);
  const hasAdAccountAssigned = useSelector(selectAdAccountAssigned);
  const accessAllAdAccounts = useSelector(selectAccessAllAdAccounts);

  const [searchParams] = useSearchParams();
  const adsAccountIdFromParams = searchParams.get("adsAccountId");
  const params = new URLSearchParams(window.location.search);

  const [selectedAdsAccountId, setSelectedAdsAccountId] = useState("");
  const [selectedAccountData, setSelectedAccountData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(7, "days").toDate());
  const [endDate, setEndDate] = useState(new Date());
  const [datePreset, setDatePreset] = useState("last_7d");
  const [selectedTab, setSelectedTab] = useState(parseInt(searchParams.get("tab") || "0", 10));
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [afterValue, setAftervalue] = useState("");

  const TabBgNormal = useColorModeValue("brand.100", "brand.900");
  const tabBorderColor = useColorModeValue("brand.200", "brand.400");
  const activeTabBg = useColorModeValue("brand.50", "brand.800");
  const inactiveTabText = useColorModeValue("gray.600", "gray.400");
  const iconHoverBg = useColorModeValue("brand.100", "brand.400");
  const textColor = useColorModeValue("brand.900", "brand.500");
  const DarkText = useColorModeValue("text.light", "text.dark");
  const textColorSub = useColorModeValue("brand.300", "brand.700");
  const tabFontSize = useBreakpointValue({ base: "sm", md: "md" });
  const tabPaddingX = useBreakpointValue({ base: 2, md: 4 });
  const tabPaddingY = useBreakpointValue({ base: 2, md: 3 });
  const iconSize = useBreakpointValue({ base: 4, md: 6 });

  const { data: accountsCount } = useGetBmAdAccountsCountQuery();
  const totalCount = accountsCount?.summary?.total_count ?? 20;
  const isSearching = !!searchTerm?.trim();
  const effectivePageSize = isSearching ? String(totalCount) : "20";

  const {
    data: AdAccountData,
    refetch: refetchAdaccountData,
    isLoading: isAdAccountLoading,
    isFetching: isAdAccountFetching,
    error: AdAccountError,
    isError: isAdaccountError,
  } = useGetBmAdAccountsQuery({
    search: searchTerm ? searchTerm : "",
    pageSize: effectivePageSize,
    after: searchTerm ? "" : afterValue,
  });

  useEffect(() => {
    if (AdAccountError) {
      toast({
        position: "top-right",
        title: "Error",
        description: AdAccountError?.data?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });

      navigate("/launchpad");
    }
  }, [AdAccountError]);

  useEffect(() => {
    if (businessId) {
      refetchAdaccountData();
      setSearchTerm("");
    }
  }, [businessId]);

  useEffect(() => {
    if (hasSearched && searchTerm === "") {
      refetchAdaccountData();
    }
  }, [searchTerm]);

  useEffect(() => {
    // If accountTimezone is available, calculate dates based on that
    if (selectedAccountData?.accountTimezone) {
      const accountTimezone = selectedAccountData?.accountTimezone;
      const initialStartDate = toZonedTime(moment().subtract(7, "days").toDate(), accountTimezone);
      const initialEndDate = toZonedTime(new Date(), accountTimezone);

      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
    }
  }, [selectedAccountData]);

  useEffect(() => {
    const storedAccount = JSON.parse(localStorage.getItem("selectedAdsAccount"));

    if (selectedAdsAccountId) {
      return;
    }

    if (adsAccountIdFromParams) {
      setSelectedAdsAccountId(adsAccountIdFromParams);
      return;
    }

    if (storedAccount) {
      setSelectedAdsAccountId(storedAccount?.value);
      return;
    }

    if (selectedAdAccounts?.length > 0) {
      const defaultAccountId = selectedAdAccounts[0]?.account_id;
      setSelectedAdsAccountId(defaultAccountId);
    }
  }, [selectedAdAccounts, adsAccountIdFromParams]);

  useEffect(() => {
    // Handle URL parameters for date and ads account ID on initial load
    const dateParam = searchParams.get("date");
    if (dateParam) {
      try {
        const decodedDateParam = decodeURIComponent(dateParam);
        const [dateRange, preset] = decodedDateParam.split(",");
        const [urlStartDate, urlEndDate] = dateRange.split("_");

        // Set the state based on the URL parameters
        setStartDate(urlStartDate ? moment(urlStartDate, "YYYY-MM-DD").toDate() : null);
        setEndDate(urlEndDate ? moment(urlEndDate, "YYYY-MM-DD").toDate() : null);
        setDatePreset(preset || "");
      } catch (error) {
        console.error("Error parsing date from URL:", error);
      }
    }
  }, []);

  // Read values from URL on load or change
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const campaignIdFromUrl = params.get("campaignIds");
    const objectiveFromUrl = params.get("objective");
    const promotionTypeFromUrl = params.get("promotion_type");
    const adsetIdFromUrl = params.get("adsetIds");

    // Fallback to localStorage
    if (!campaignIdFromUrl || !objectiveFromUrl) {
      const cachedCampaign = localStorage.getItem("cachedCampaign");
      if (cachedCampaign) {
        try {
          const parsed = JSON.parse(cachedCampaign);
          dispatch(setSelectedCampaignId(parsed));
        } catch (e) {
          console.error("Invalid cachedCampaign JSON", e);
        }
      }
    } else {
      dispatch(setSelectedCampaignId({
        campaign_id: campaignIdFromUrl,
        objective: objectiveFromUrl,
        smart_promotion_type: promotionTypeFromUrl,
      }));
    }

    if (adsetIdFromUrl) {
      dispatch(setSelectedAdsetId(adsetIdFromUrl));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    if (selectedCampaignId?.campaign_id) {
        localStorage.setItem("cachedCampaign", JSON.stringify(selectedCampaignId));
    }
  }, [selectedCampaignId]);


  useEffect(() => {
    if (selectedAdsAccountId) {
      const account = selectedAdAccounts?.find((acc) => acc?.account_id === selectedAdsAccountId);
      setSelectedAccountData(account);
    }
  }, [selectedAdsAccountId, selectedAccountData, AdAccountData, selectedAdAccounts]);

  const handleDateRangeChange = (startDate, endDate, datePreset) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setDatePreset(datePreset);
  };

  // Update the URL when startDate, endDate, or datePreset change
  useEffect(() => {
    const publishDestination = localStorage.getItem("sourcePublish");

    // Always include the selected adsAccountId first
    if (selectedAdsAccountId && publishDestination !== "publish") {
      params.set("adsAccountId", selectedAdsAccountId);
    } else if (selectedAdsAccountId && !adsAccountIdFromParams) {
      params.set("adsAccountId", selectedAdsAccountId);
    }

    // Setting the date parameter in the format: 2024-10-26_2024-11-25%2Clast_30d
    const formattedDate =
      startDate && endDate
        ? `${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format(
            "YYYY-MM-DD"
          )},${datePreset}`
        : datePreset;

    if (formattedDate) {
      params.set("date", encodeURIComponent(formattedDate));
    }

    if (selectedCampaignId) {
      params.set(
        "campaignIds",
        selectedCampaignId?.campaign_id ? selectedCampaignId?.campaign_id : selectedCampaignId
      );
      params.set("objective", selectedCampaignId?.objective);
      params.set("promotion_type", selectedCampaignId?.smart_promotion_type);
    }

    if (selectedAdsetId) {
      params.set(
        "adsetIds",
        selectedAdsetId?.adset_id ? selectedAdsetId?.adset_id : selectedAdsetId
      );
    }
    if (selectedAdsId) {
      params.set("adsIds", selectedAdsId);
    }
    if (selectedTab) {
      params.set("tab", selectedTab);
    }
    // Update the URL with the new parameters
    navigate({ search: params.toString() }, { replace: true });
  }, [
    startDate,
    endDate,
    datePreset,
    selectedAdsAccountId,
    selectedCampaignId,
    selectedAdsetId,
    selectedAdsId,
  ]);

  const resetSelectionsAndParams = () => {
    // Remove parameters from the URL to reset the state
    dispatch(setCampaignsForInsights([]));
    dispatch(setAdsetsForInsights([]));
    dispatch(setAfter(""));
    dispatch(setBefore(""));
    dispatch(setCurrentPage(0));
    dispatch(setPresentPage(1));
    localStorage.removeItem("after");
    localStorage.removeItem("before");
    localStorage.removeItem("currentPage");
  };

  const handleRefresh = useCallback(() => {
    setShouldRefresh(true);
  }, []);

  const handleRefreshComplete = () => {
    setShouldRefresh(false);
  };

  const handleAdaccountChange = (value) => {
    if (value?.value !== selectedAdsAccountId) {
      const newAccountId = value?.value;
      setSelectedAdsAccountId(newAccountId);
  
      // === Reset Redux state ===
      dispatch(setSelectedCampaignId(null));
      dispatch(setSelectedCampaigns(null));
      dispatch(setSelectedAdsetId(null));
      dispatch(setSelectedAdset(null));
      dispatch(setSelectedAdsId(null));
      resetSelectionsAndParams();
  
      // === Clean URL params ===
      const cleanedParams = new URLSearchParams();
  
      // Preserve only adsAccountId, date, and tab (if present)
      cleanedParams.set("adsAccountId", newAccountId);
  
      const currentTab = searchParams.get("tab");
      const currentDate = searchParams.get("date");
  
      if (currentTab) cleanedParams.set("tab", currentTab);
      if (currentDate) cleanedParams.set("date", currentDate);
  
      navigate({ search: cleanedParams.toString() }, { replace: true });
  
      // Also update tracking key for URL-based logic (optional)
      localStorage.setItem("previousAdsAccountId", newAccountId);
  
      // Remove campaign from cache
      localStorage.removeItem("cachedCampaign");
    }
  };
  

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleLoadMore = (after) => {
    setAftervalue(after);
  };

  return (
    <SidebarWithHeader>
      {isAdaccountError ? (
        <Flex justify="center" align="center" height="80vh">
          <Text color="red.500">
            {/* {AdAccountError?.data?.message} */}
            Please disconnect and reconnect your Facebook Business Manager account to continue
            accessing the platform.
          </Text>
        </Flex>
      ) : (
        <>
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={4}
            align={{ base: "stretch", md: "center" }}
            justify={{ base: "flex-start", md: "space-between" }}
            mb={4}
          >
            {/* Left Section */}
            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={3}
              align={{ base: "flex-start", sm: "center" }}
              flexWrap="wrap"
            >
              <Text fontWeight="bold" color={textColor}>
                Ad accounts
              </Text>

              <Flex alignItems={'center'} gap={2}>
                <SearchAdaccount
                  onInitialisation={setSelectedAdsAccountId}
                  accountOptions={AdAccountData?.data}
                  onAccountChange={handleAdaccountChange}
                  onSearchInputChange={handleSearchChange}
                  onSearchInputTouched={setHasSearched}
                  AdsAccountIdFromParams={adsAccountIdFromParams}
                  isAdAccountLoading={isAdAccountLoading}
                  isAdAccountFetching={isAdAccountFetching}
                  pageMeta={AdAccountData?.meta}
                  onLoadMore={handleLoadMore}
                  context={"campaign"}
                />

                <Tooltip
                  borderRadius={5}
                  label={
                    <Box fontSize="sm" p="5px">
                      <Text>
                        Select an Ad Account from the dropdown to view the campaign’s performance.
                      </Text>
                    </Box>
                  }
                  fontSize="md"
                >
                  <InfoOutlineIcon pointerEvents="all" />
                </Tooltip>
              </Flex>
            </Flex>

            {/* Right Section */}
            <Flex
              mt={{ base: 3, md: 0 }}
              align="center"
              gap={3}
              wrap="wrap"
              justify={{ base: "flex-start", md: "flex-end" }}
            >
              <Flex alignItems={'center'} gap={2}>
                <Tooltip label="Refresh Table Data" fontSize="sm" p="5px">
                  <Flex>
                    <TbRefresh cursor="pointer" onClick={handleRefresh} />
                  </Flex>
                </Tooltip>

                {startDate && endDate ? (
                    <DateRangeSelector
                      onDateRangeChange={handleDateRangeChange}
                      initialStartDate={startDate}
                      initialEndDate={endDate}
                      initialDatePreset={datePreset}
                      timeZone={selectedAccountData?.timezone_name}
                    />
                ) : null}
              </Flex>
            </Flex>
          </Flex>

          <Flex alignItems={"center"} mb={4}>
            <Text fontSize={"14px"} color={textColorSub}>
              Manage, monitor, and optimize your Ad campaigns across multiple accounts.
            </Text>
          </Flex>

          {isAdAccountLoading? 
              null
          :
          <>
            {/* Tabs always visible, and can switch between tabs */}
            <Tabs
              index={selectedTab}
              onChange={(index) => {
                setSelectedTab(index);
                params.set("tab", index);
                params.set("page_id", 0);
                navigate({ search: params.toString() }, { replace: true });
                resetSelectionsAndParams();
              }}
              p={0}
              colorScheme="brand"
              variant="unstyled"
            >
              <TabList borderBottom="1px solid" borderColor={tabBorderColor}>
                {[0, 1, 2].map((tabIndex) => {
                  const isActive = selectedTab === tabIndex;

                  const tabLabels = [
                    {
                      label: "Campaigns",
                      icon: MdFolderOpen,
                      selectedId: selectedCampaignId,
                      clearAction: () => {
                        dispatch(setSelectedCampaignId(null));
                        localStorage.removeItem("cachedCampaign");
                        dispatch(setSelectedCampaigns(null));
                        params.delete("campaignIds");
                        params.delete("objective");
                        params.delete("promotion_type");
                      },
                    },
                    {
                      label: "Ad sets",
                      icon: MdWindow,
                      selectedId: selectedAdsetId,
                      clearAction: () => {
                        dispatch(setSelectedAdsetId(null));
                        dispatch(setSelectedAdset(null));
                        params.delete("adsetIds");
                      },
                    },
                    {
                      label: "Ads",
                      icon: MdOutlineFolderSpecial,
                      selectedId: selectedAdsId,
                      clearAction: () => {
                        dispatch(setSelectedAdsId(null));
                        params.delete("adsIds");
                      },
                    },
                  ];

                  const { label, icon, selectedId, clearAction } = tabLabels[tabIndex];

                  const labelText =
                    tabIndex === 1 && selectedCampaignId && !selectedAdsetId
                      ? "Ad sets for 1 Campaign"
                      : tabIndex === 2 && selectedAdsetId
                      ? "Ads for 1 Ad set"
                      : tabIndex === 2 && selectedCampaignId
                      ? "Ads for 1 Campaign"
                      : label;

                  return (
                    <Tab
                      key={tabIndex}
                      fontWeight="light"
                      flex={1}
                      px={tabPaddingX}
                      py={tabPaddingY}
                      fontSize={tabFontSize}
                      color={isActive ? inactiveTabText : DarkText}
                      bg={isActive ? activeTabBg : TabBgNormal}
                      border="1px solid"
                      borderTop={isActive ? activeTabBg : TabBgNormal}
                      borderBottom={isActive ? "none" : TabBgNormal}
                      borderRight={isActive ? "none" : TabBgNormal}
                      borderLeft={isActive ? "none" : TabBgNormal}
                      borderColor={isActive ? textColor : activeTabBg}
                    >
                      <Flex
                        direction={{ base: "column", md: "row" }}
                        justify="space-between"
                        align="center"
                        width="100%"
                        gap={2}
                      >
                        <Flex align="center">
                          <Icon as={icon} boxSize={iconSize} mr={2} />
                          <Text isTruncated>{labelText}</Text>
                        </Flex>

                        {selectedId && (
                          <Flex
                            align="center"
                            bg="brand.500"
                            color="whiteAlpha.900"
                            p={1}
                            borderRadius="md"
                          >
                            <Text fontSize="sm" fontWeight="normal" mr={2}>
                              1 selected
                            </Text>
                            <Icon
                              as={MdClose}
                              boxSize={4}
                              cursor="pointer"
                              onClick={() => {
                                clearAction();
                                localStorage.removeItem("sourcePublish");
                                navigate({ search: params.toString() }, { replace: true });
                              }}
                              _hover={{
                                bg: iconHoverBg,
                                borderRadius: "full",
                              }}
                            />
                          </Flex>
                        )}
                      </Flex>
                    </Tab>
                  );
                })}
              </TabList>
              {hasAdAccountAssigned && selectedAdsAccountId ? (
                <>
                  <TabPanels>
                    <TabPanel px={0}>
                      {selectedTab === 0 && (
                        <>
                          {accessAllAdAccounts ? (
                            <AllCampaignsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              setSelectedTab={setSelectedTab}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          ) : (
                            <CampaignsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              setSelectedTab={setSelectedTab}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          )}
                        </>
                      )}
                    </TabPanel>
                    <TabPanel px={0}>
                      {selectedTab === 1 ? (
                        <>
                          {accessAllAdAccounts ? (
                            <AllAdSetsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              setSelectedTab={setSelectedTab}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          ) : (
                            <AdSetsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              setSelectedTab={setSelectedTab}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          )}
                        </>
                      ) : null}
                    </TabPanel>
                    <TabPanel px={0}>
                      {selectedTab === 2 && (
                        <>
                          {accessAllAdAccounts ? (
                            <AllAdsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              setSelectedTab={setSelectedTab}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          ) : (
                            <AdsTable
                              adsAccountData={selectedAccountData}
                              startDate={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
                              endDate={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
                              datePreset={datePreset}
                              onRefresh={shouldRefresh}
                              onRefreshComplete={handleRefreshComplete}
                            />
                          )}
                        </>
                      )}
                    </TabPanel>
                  </TabPanels>
                </>
              ) : (
                <Flex justify="center" align="center" height="80vh">
                  <Text>No data available</Text>
                </Flex>
              )}
            </Tabs>
            </>
          }
        </>
      )}
    </SidebarWithHeader>
  );
}

export default CampaignsForClient;